import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import { DepartmentPicker, LocationPicker } from 'components/Pickers';
import { AssignmentOperator, AssignmentInput } from 'api';

import TargetUsers from './TargetUsers';
import { RemoveIcon } from 'components';

enum DefineUserValues {
  Filter = 'filter',
  All = 'all',
}

const useStyles = makeStyles(theme => ({
  divider: {
    margin: theme.spacing(2, -5, 2, 0),
  },
  header: {
    margin: theme.spacing(4, 0, 0),
  },
  userCount: {
    fontSize: theme.spacing(2.5),
  },
  label: {
    fontSize: theme.spacing(2.5),
    fontWeight: 'bold',
  },
}));

const DefineUsers: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [radioValue, setRadioValue] = useState(DefineUserValues.Filter || 'filter');

  const { setFieldValue, values } = useFormikContext<AssignmentInput>();
  const { rule } = values;
  const { all_users, operator, department_ids, location_ids } = rule;

  useEffect(() => {
    setRadioValue(all_users ? DefineUserValues.All : DefineUserValues.Filter);
  }, [all_users]);

  function onChangeRadio(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setRadioValue(value);
    setFieldValue('rule.all_users', value !== 'filter');
  }

  function onChangeOperator(event: ChangeEvent<HTMLSelectElement>) {
    const { value } = event.target;
    setFieldValue('rule.operator', value);
  }

  return (
    <Fragment>
      <Grid container justify="space-between" alignItems="baseline">
        <Grid item>
          <Typography variant="h5" color="textSecondary" className={classes.header}>
            {t('admin:defineUsers')}
          </Typography>
        </Grid>
      </Grid>

      <Divider className={classes.divider} />

      <RadioGroup row onChange={onChangeRadio} value={radioValue}>
        <Box mr={4}>
          <FormControlLabel
            classes={{ label: classes.label }}
            label={`${t('common:department')} & ${t('common:location')}`}
            control={
              <Radio
                color="primary"
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
              />
            }
            value={DefineUserValues.Filter}
          />
        </Box>

        <FormControlLabel
          classes={{ label: classes.label }}
          label={`${t('common:all')} ${t('common:user_plural')}`}
          control={
            <Radio
              color="primary"
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
            />
          }
          value={DefineUserValues.All}
        />
      </RadioGroup>

      <Box pl={4} pt={3} pb={5}>
        <Grid container spacing={2}>
          {!all_users && (
            <Fragment>
              <Grid item container alignItems="center">
                <Grid item xs={2}>
                  <Typography variant="body2">{t('admin:departments')}</Typography>
                </Grid>
                <Grid item xs={9}>
                  <DepartmentPicker
                    selectedDepartmentIds={department_ids}
                    onDepartmentChange={ids => setFieldValue('rule.department_ids', ids)}
                  />
                </Grid>
                {!!department_ids.length && (
                  <Grid item xs={1}>
                    <Box pl={1}>
                      <RemoveIcon onClick={() => setFieldValue('rule.department_ids', [])} />
                    </Box>
                  </Grid>
                )}
              </Grid>

              <Grid item container>
                <Grid item xs={2} />
                <Grid item xs={3}>
                  <FormControl>
                    <Tooltip
                      placement="right"
                      title={t('admin:assignmentDefineUsersTooltip')}
                      disableHoverListener={!!department_ids.length && !!location_ids.length}
                    >
                      <Select
                        disabled={!department_ids.length || !location_ids.length}
                        open={open}
                        value={operator}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        onChange={onChangeOperator}
                      >
                        <MenuItem value={AssignmentOperator.Or}>{t('admin:or')}</MenuItem>
                        <MenuItem value={AssignmentOperator.And}>{t('admin:and')}</MenuItem>
                      </Select>
                    </Tooltip>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid item container alignItems="center">
                <Grid item xs={2}>
                  <Typography variant="body2">{t('admin:locations')}</Typography>
                </Grid>
                <Grid item xs={9}>
                  <LocationPicker
                    selectedLocationIds={location_ids}
                    onLocationChange={ids => setFieldValue('rule.location_ids', ids)}
                  />
                </Grid>
                {!!location_ids.length && (
                  <Grid item xs={1}>
                    <Box pl={1}>
                      <RemoveIcon onClick={() => setFieldValue('rule.location_ids', [])} />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Fragment>
          )}

          <TargetUsers />
        </Grid>
      </Box>
    </Fragment>
  );
};

export default DefineUsers;
